import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PackageModal from "./components/PackageModal";
import {
  usePackages,
  createPackage,
  updatePackage,
  deletePackage,
  logAudit,
  type Package,
  type PackageInput,
} from "@/hooks/useAdmin";

export default function AdminPaketler() {
  const { data, loading, error, reload } = usePackages();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Package | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const list = data ?? [];
  const activeCount = list.filter((p) => p.active).length;

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (pkg: Package) => {
    setEditing(pkg);
    setModalOpen(true);
  };

  const handleSubmit = async (input: PackageInput) => {
    if (editing) {
      const err = await updatePackage(editing.id, input);
      if (!err) await logAudit("paket_guncelle", "paket", input.name);
      return err;
    }
    const err = await createPackage(input);
    if (!err) await logAudit("paket_olustur", "paket", input.name);
    return err;
  };

  const handleDelete = async (pkg: Package) => {
    if (deletingId) return;
    setDeletingId(pkg.id);
    const err = await deletePackage(pkg.id);
    setDeletingId(null);
    if (!err) {
      await logAudit("paket_sil", "paket", pkg.name);
      reload();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Paketler"
          description="Üyelik paketlerini ve fiyatlandırmayı yönet."
        />
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
        >
          <i className="ri-add-line" aria-hidden="true"></i>
          Yeni Paket
        </button>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-vip-crown-line"
          title="Henüz paket tanımlı değil"
          description='"Yeni Paket" ile üyelik paketlerini ve fiyatlarını oluştur.'
        />
      ) : (
        <>
          <p className="text-sm text-foreground-500">
            {list.length} paket · {activeCount} aktif
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {list.map((pkg) => (
              <div
                key={pkg.id}
                className={`rounded-lg border bg-background-50 p-5 ${
                  pkg.active ? "border-background-200" : "border-background-200 opacity-60"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-base text-foreground-950 truncate">
                      {pkg.name}
                    </h3>
                    <p className="mt-1 font-heading font-bold text-2xl text-primary-700">
                      {pkg.price > 0 ? `₺${pkg.price.toLocaleString("tr-TR")} / ay` : "Ücretsiz"}
                    </p>
                    {pkg.annualEnabled && pkg.price>0 && <p className="text-sm text-primary-700">Yıllık {((Math.round(pkg.price*100)*12*(100-pkg.annualDiscountPercent)/100)/100).toLocaleString('tr-TR',{minimumFractionDigits:2,maximumFractionDigits:2})} TL · %{pkg.annualDiscountPercent} indirim</p>}
                    {pkg.durationDays > 0 && (
                      <p className="text-xs text-foreground-500 mt-0.5">1 ay{pkg.annualEnabled ? " / 12 ay" : ""}</p>
                    )}
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      pkg.active ? "bg-accent-100 text-accent-800" : "bg-background-100 text-foreground-500"
                    }`}
                  >
                    {pkg.active ? "Aktif" : "Pasif"}
                  </span>
                </div>

                <p className="mt-3 text-sm">Meraklı: {pkg.merakliEnabled?'Dahil':'Dahil değil'} · Toplantı: {pkg.meetingEnabled?'Dahil':'Dahil değil'}</p>
                {pkg.description && (
                  <p className="mt-3 text-sm text-foreground-600 line-clamp-2">{pkg.description}</p>
                )}

                {pkg.features.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {pkg.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-foreground-600">
                        <i className="ri-check-line text-accent-700" aria-hidden="true"></i>
                        {f}
                      </li>
                    ))}
                    {pkg.features.length > 4 && (
                      <li className="text-xs text-foreground-400">
                        +{pkg.features.length - 4} özellik daha
                      </li>
                    )}
                  </ul>
                )}

                <div className="mt-4 pt-4 border-t border-background-200 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(pkg)}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-background-100 text-foreground-700 whitespace-nowrap hover:bg-background-200 transition-colors"
                  >
                    <i className="ri-pencil-line" aria-hidden="true"></i>
                    Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(pkg)}
                    disabled={deletingId === pkg.id}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-foreground-500 whitespace-nowrap hover:bg-accent-100 hover:text-accent-700 disabled:opacity-50 transition-colors"
                  >
                    {deletingId === pkg.id ? (
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    ) : (
                      <i className="ri-delete-bin-line" aria-hidden="true"></i>
                    )}
                    Sil
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <PackageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        packageItem={editing}
        onSaved={reload}
        onSubmit={handleSubmit}
      />
    </div>
  );
}